// <s-product-finder> runs the branching tap quiz inside #product-finder (a native dialog).
// Answers carry data-next (another question) or data-result (a result panel). Result
// panels list product handles; matching <template data-finder-product> cards are cloned in.
// Opens from links to #find-my-product (ads, menus, nudge) as well as commandfor buttons.
const HASH = '#find-my-product';
const USED_KEY = 'rnu-finder-used';

class SProductFinder extends HTMLElement {
  #history = [];
  #ac = null;

  connectedCallback() {
    this.dialog = this.closest('dialog');
    if (!this.dialog) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('click', this.#onClick, { signal });
    this.dialog.addEventListener('click', this.#onBackdrop, { signal });
    this.dialog.addEventListener('close', () => this.#reset(), { signal });
    document.addEventListener('click', this.#onHashLink, { signal });
    window.addEventListener('hashchange', this.#openFromHash, { signal });

    this.#show('goal');
    this.#openFromHash();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  open() {
    if (!this.dialog.open) this.dialog.showModal();
  }

  #openFromHash = () => {
    if (window.location.hash !== HASH) return;
    this.open();
    history.replaceState(null, '', window.location.pathname + window.location.search);
  };

  #onHashLink = (event) => {
    const link = event.target.closest?.('a[href$="' + HASH + '"]');
    if (!link) return;
    event.preventDefault();
    this.open();
  };

  #onBackdrop = (event) => {
    if (event.target === this.dialog) this.dialog.close();
  };

  #onClick = (event) => {
    const option = event.target.closest('[data-next], [data-result]');
    if (option) {
      if (option.dataset.next) this.#go(option.dataset.next);
      else this.#showResult(option.dataset.result);
      return;
    }
    if (event.target.closest('[data-finder-back]')) {
      this.#history.pop();
      const previous = this.#history.pop();
      if (previous) this.#show(previous);
      return;
    }
    if (event.target.closest('[data-finder-restart]')) {
      this.#reset();
      return;
    }
    const add = event.target.closest('[data-finder-add]');
    if (add) this.#add(add);
  };

  #go(stepId) {
    this.#show(stepId);
  }

  #panels() {
    return this.querySelectorAll('[data-step], [data-result-panel]');
  }

  #show(stepId) {
    this.#panels().forEach((el) => { el.hidden = el.dataset.step !== stepId; });
    this.#history.push(stepId);
    this.#syncChrome(false);
    this.querySelector(`[data-step="${stepId}"] button`)?.focus({ preventScroll: true });
  }

  #showResult(resultId) {
    const panel = this.querySelector(`[data-result-panel="${resultId}"]`);
    if (!panel) return;
    const list = panel.querySelector('[data-handles]');
    if (list && !list.childElementCount) {
      list.dataset.handles.split(',').forEach((handle) => {
        const template = this.querySelector(`template[data-finder-product="${handle.trim()}"]`);
        if (template) list.append(template.content.cloneNode(true));
      });
    }
    this.#panels().forEach((el) => { el.hidden = el !== panel; });
    this.#history.push('result:' + resultId);
    this.#syncChrome(true);
    panel.querySelector('p')?.setAttribute('tabindex', '-1');
    panel.querySelector('p')?.focus({ preventScroll: true });
    this.querySelector('[data-finder-body]')?.scrollTo({ top: 0 });
    try { localStorage.setItem(USED_KEY, String(Date.now())); } catch (error) { /* storage blocked */ }
  }

  #syncChrome(isResult) {
    const back = this.querySelector('[data-finder-back]');
    if (back) back.hidden = this.#history.length < 2;
    const footer = this.querySelector('[data-finder-footer]');
    if (footer) footer.hidden = !isResult;
    const progress = this.querySelector('[data-finder-progress]');
    if (progress) progress.textContent = isResult ? '' : (this.dataset.stepLabel || '').replace('__N__', String(this.#history.length));
  }

  #reset() {
    this.#history = [];
    this.#show('goal');
  }

  async #add(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = this.dataset.adding || label;
    const status = this.querySelector('[data-finder-status]');
    try {
      if (window.Shopify?.actions?.updateCart) {
        const result = await window.Shopify.actions.updateCart({ lines: [{ merchandiseId: variantId, quantity: 1 }] });
        if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
        this.dialog.close();
        await window.Shopify.actions.openCart?.();
      } else {
        const form = document.createElement('form');
        form.method = 'post';
        form.action = this.dataset.cartAddUrl;
        form.innerHTML = `<input type="hidden" name="id" value="${variantId}"><input type="hidden" name="quantity" value="1">`;
        document.body.append(form);
        form.submit();
      }
    } catch (error) {
      if (status) status.textContent = this.dataset.error || '';
    } finally {
      button.disabled = false;
      button.textContent = label;
    }
  }
}

if (!customElements.get('s-product-finder')) customElements.define('s-product-finder', SProductFinder);
